import React, {
    ComponentType,
    CSSProperties,
    useMemo,
    useRef,
    useEffect,
    useCallback,
    useLayoutEffect,
} from 'react';
import styles from './additional-info-renderer.module.css';
import { useVirtualizer } from '@tanstack/react-virtual';
import { observer } from 'mobx-react-lite';
import { useAdditionalInfos } from '../hooks/use-additional-infos/use-additional-infos';
import { AdditionalInfoView } from '../types';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { VirtualizerContext } from './use-row-virtualizer';
import { useUpdateVisibleAsRead } from './use-update-visible-read';
import { useScrollAnchor } from './use-scroll-anchor';
import { useEstimateRowSize } from './use-estimate-row-size';

// Row props that must be provided to the renderer
interface AdditionalInfoRowProps {
    currentUserId?: number;
    canDeleteInfos?: boolean;
    onOpenEvent?: (id: number) => void;
}
export interface AdditionalInfoRendererProps extends AdditionalInfoRowProps {
    height?: CSSProperties['height'];
    width?: CSSProperties['width'];
    markVisibleAsRead?: boolean;
    rowComponent: ComponentType<
        {
            data: AdditionalInfoView;
            index: number;
            agencyEventNumberToIdMap?: Record<string, number>;
            isProQA?: boolean;
        } & AdditionalInfoRowProps
    >;
    loadingComponent?: ComponentType;
    addonComponent?: ComponentType<{ height?: number; width?: number }>;
    onAllRead?: (id: number) => void;
    overscan?: number;
    forceReload?: boolean;
    isReadOnlyMode?: boolean;
    isFirstResponder?: boolean;
}

export const AdditionalInfoRenderer = observer(
    ({
        rowComponent: RowComponent,
        loadingComponent: LoadingComponent,
        addonComponent: AddonComponent,
        markVisibleAsRead = true,
        onAllRead,
        overscan = 0,
        forceReload,
        ...rest
    }: AdditionalInfoRendererProps) => {
        const scrollContainerRef = useRef<HTMLDivElement | null>(null);
        const {
            list: data,
            agencyEventNumberToIdMap,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            pageSize,
            pageFetchFailed,
            initialSocketConnectionMade,
            reloadPage,
            isPageReloading,
        } = useAdditionalInfos();

        const getItemKey = useCallback(
            (index) => {
                const isLoaderRow = index === data.length;
                return isLoaderRow ? index : data[index].id;
            },
            [data]
        );

        const { estimateRowsSize, estimateRowSize } = useEstimateRowSize(data, scrollContainerRef);

        const rowVirtualizer = useVirtualizer({
            count: hasNextPage ? data.length + 1 : data.length,
            getItemKey,
            getScrollElement: () => scrollContainerRef.current,
            estimateSize: estimateRowSize,
            overscan,
        });

        useScrollAnchor({
            data,
            estimateRowsSize,
            rowVirtualizer,
        });

        const virtualItems = rowVirtualizer.getVirtualItems();

        const lastVirtualItem = virtualItems[virtualItems.length - 1];
        useEffect(() => {
            if (!lastVirtualItem) {
                return;
            }

            if (
                lastVirtualItem.index >= data.length - 1 &&
                hasNextPage &&
                !isFetchingNextPage &&
                !pageFetchFailed &&
                initialSocketConnectionMade
            ) {
                fetchNextPage();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            data.length,
            fetchNextPage,
            hasNextPage,
            initialSocketConnectionMade,
            isFetchingNextPage,
            pageSize,
            lastVirtualItem,
        ]);

        useLayoutEffect(() => {
            // Something is causing the scrollElement div scrollTop (the div which contains the narrative scrollbar) to change
            // to 0 when a different event window is focussed and then this event window is focussed. The rowVirtualizer is not
            // aware of this and its scrollOffset remains it was previously (i.e. not 0). This causes the scrollContent div to
            // scroll to a blank area. This is a hack which causes the rowVirtualizer to scroll the scrollContent div to the offset
            // that the rowVirtualizer thinks it's already at after rendering.
            if (
                scrollContainerRef.current !== null &&
                rowVirtualizer &&
                scrollContainerRef.current.scrollTop !== rowVirtualizer.scrollOffset
            ) {
                rowVirtualizer.scrollToOffset(rowVirtualizer.scrollOffset, {
                    align: 'start',
                });
            }
        });

        const totalSize = rowVirtualizer.getTotalSize();
        const fullHeightScrollContentStyle = useMemo(
            (): CSSProperties => ({
                height: totalSize,
                width: '100%',
                position: 'relative',
            }),
            [totalSize]
        );

        const virtualItemsStart = virtualItems[0]?.start;
        const translateVirtualItemsStyle = useMemo(
            (): CSSProperties =>
                virtualItemsStart === undefined
                    ? {}
                    : {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualItemsStart}px)`,
                      },
            [virtualItemsStart]
        );

        useUpdateVisibleAsRead(markVisibleAsRead, rowVirtualizer);

        useEffect(() => {
            if (forceReload) {
                reloadPage();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [forceReload]);

        return (
            <VirtualizerContext.Provider value={rowVirtualizer}>
                <div className={styles['addon-component-wrapper']}>
                    <div
                        ref={scrollContainerRef}
                        className={styles['scroll-container']}
                        data-testid="ADDITIONAL_INFOS_SCROLL_CONTAINER"
                    >
                        <div style={fullHeightScrollContentStyle}>
                            <div style={translateVirtualItemsStyle}>
                                {isPageReloading ? (
                                    <LoadingIndicator />
                                ) : (
                                    virtualItems.map((virtualRow) => {
                                        const isLoaderRow = virtualRow.index > data.length - 1;
                                        return (
                                            <div
                                                key={virtualRow.key}
                                                data-index={virtualRow.index}
                                                ref={rowVirtualizer.measureElement}
                                            >
                                                {!isLoaderRow ? (
                                                    <RowComponent
                                                        data={data[virtualRow.index]}
                                                        agencyEventNumberToIdMap={
                                                            agencyEventNumberToIdMap
                                                        }
                                                        index={virtualRow.index}
                                                        {...rest}
                                                    />
                                                ) : (
                                                    !pageFetchFailed &&
                                                    (LoadingComponent ? (
                                                        <LoadingComponent />
                                                    ) : (
                                                        <LoadingIndicator />
                                                    ))
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                    {AddonComponent && <AddonComponent />}
                </div>
            </VirtualizerContext.Provider>
        );
    }
);

export default AdditionalInfoRenderer;
